<script setup>
// Vehicle information fields; v-model is bound to the step's model state
const model = defineModel({
  type: Object,
  default: () => ({})
});

const vehicleTypeOptions = [
  { type: 'SEDAN', capacity: 4, label: 'Sedan (4 passengers)' },
  { type: 'SUV', capacity: 6, label: 'SUV (6 passengers)' },
  { type: 'SUV', capacity: 4, label: 'SUV (4 passengers)' },
  { type: 'MINIVAN', capacity: 6, label: 'Minivan (6 passengers)' }
];

const onVehicleTypeChange = (event) => {
  const selectedValue = event.target.value;
  const option = vehicleTypeOptions.find(opt => `${opt.type}-${opt.capacity}` === selectedValue);
  if (option) {
    model.value.vehicleType = option.type;
    model.value.capacity = option.capacity;
  }
};

const maxYear = 2027;
</script>

<template>
  <div class="driver-onboarding-step driver-onboarding-step-vehicle">
    <div class="row g-3">
      <!-- Vehicle Make -->
      <div class="col-md-6">
        <label class="form-label f-w-600" for="vehicle-make-input">Vehicle Make</label>
        <input id="vehicle-make-input" class="form-control" type="text" v-model="model.make" placeholder="e.g., Toyota" required>
        <div id="vehicle-make-error" class="invalid-feedback d-none">Please enter your vehicle make.</div>
      </div>

      <!-- Model -->
      <div class="col-md-6">
        <label class="form-label f-w-600" for="vehicle-model-input">Vehicle Model</label>
        <input id="vehicle-model-input" class="form-control" type="text" v-model="model.model" placeholder="e.g., Corolla" required>
        <div id="vehicle-model-error" class="invalid-feedback d-none">Please enter your vehicle model.</div>
      </div>

      <!-- Color -->
      <div class="col-md-6">
        <label class="form-label f-w-600" for="vehicle-color-input">Color</label>
        <input id="vehicle-color-input" class="form-control" type="text" v-model="model.color" placeholder="e.g., Black" required>
        <div id="vehicle-color-error" class="invalid-feedback d-none">Please enter your vehicle color.</div>
      </div>

      <!-- Year -->
      <div class="col-md-6">
        <label class="form-label f-w-600" for="vehicle-year-input">Year</label>
        <input id="vehicle-year-input" class="form-control" type="number" v-model.number="model.year" :min="1980" :max="maxYear" placeholder="e.g., 2018" required>
        <small class="text-muted">Year must be between 1980 and {{ maxYear }}.</small>
        <div id="vehicle-year-error" class="invalid-feedback d-none">Please enter a valid vehicle year (1980-{{ maxYear }}).</div>
      </div>

      <!-- License Plate Number -->
      <div class="col-md-6">
        <label class="form-label f-w-600" for="plate-number-input">License Plate Number</label>
        <input id="plate-number-input" class="form-control" type="text" v-model="model.plateNumber" placeholder="e.g., ABC-1234" required>
        <div id="plate-number-error" class="invalid-feedback d-none">Please enter your license plate number.</div>
      </div>

      <!-- Vehicle Type -->
      <div class="col-md-6">
        <label class="form-label f-w-600" for="vehicle-type-select">Vehicle Type</label>
        <select id="vehicle-type-select" class="form-select" @change="onVehicleTypeChange"
                :value="model.vehicleType && model.capacity ? `${model.vehicleType}-${model.capacity}` : ''" required>
          <option value="" disabled>Select vehicle type</option>
          <option v-for="(vt, idx) in vehicleTypeOptions" :key="idx" :value="`${vt.type}-${vt.capacity}`">
            {{ vt.label }}
          </option>
        </select>
        <div class="form-text">Choose from the available vehicle categories.</div>
        <div id="vehicle-type-error" class="invalid-feedback d-none">Please select a vehicle type.</div>
      </div>

      <!-- Odometer -->
      <div class="col-md-6">
        <label class="form-label f-w-600" for="vehicle-odometer-input">Odometer (km)</label>
        <input id="vehicle-odometer-input" class="form-control" type="number" v-model.number="model.odometer" min="0" max="200000" placeholder="e.g., 85000" required>
        <small class="text-muted">Current odometer reading in kilometres (cannot exceed 200,000 km).</small>
        <div id="vehicle-odometer-error" class="invalid-feedback d-none">Please enter a valid odometer reading (0-200,000 km).</div>
      </div>
    </div>
  </div>
</template>

<style scoped>
</style>
